'use client';

import { lazy, Suspense, type FC, type ReactNode } from 'react';

import { CheckIcon, CopyIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { cn } from '@/lib/utils';

interface CodeBlockProps {
  code: string;
  language?: string;
  className?: string;
  children?: ReactNode;
}

/**
 * The grammars we ship — an allowlist, not a lookup table. Anything unmapped stays plain rather than
 * being guessed at, which is both why the chat's `arguments`/`result` blocks never load the
 * highlighter and why a wrong grammar can't mis-colour code. Adding a language here means adding its
 * grammar import in code-block-highlighted.tsx too.
 */
const HIGHLIGHT_LANGUAGES = new Set(['html', 'javascript', 'tsx']);

const PRE_CLASSES =
  'overflow-x-auto rounded-b-lg bg-slate-950 p-4 font-mono text-sm leading-relaxed whitespace-pre-wrap text-slate-50';

/** Lazy so the grammars stay out of the chat bundle — see code-block-highlighted.tsx. */
const CodeBlockHighlighted = lazy(() => import('./code-block-highlighted'));

export const CodeBlock: FC<CodeBlockProps> = ({ code, language, className, children }) => {
  const t = useTranslations();
  const { isCopied, copyToClipboard } = useCopyToClipboard();

  const onCopy = () => {
    if (!code || isCopied) return;
    copyToClipboard(code);
  };

  const grammar = language?.toLowerCase();
  const highlighted = grammar ? HIGHLIGHT_LANGUAGES.has(grammar) : false;
  // `children` wins when given (callers that render their own body), and an unrecognised language
  // stays plain text — only a known grammar gets tokenised.
  const plain = children !== undefined || !highlighted;

  return (
    <div className="relative">
      <div className="flex items-center justify-between gap-4 rounded-t-lg bg-muted-foreground/15 px-4 py-2 text-sm font-semibold text-foreground dark:bg-muted-foreground/20">
        {language && <span className="lowercase">{language}</span>}
        {!language && <span />}
        <TooltipIconButton tooltip={t('additionalUi.copy')} side="top" onClick={onCopy}>
          {!isCopied && <CopyIcon />}
          {isCopied && <CheckIcon />}
        </TooltipIconButton>
      </div>
      {plain ? (
        <pre className={cn(PRE_CLASSES, className)}>{children ?? <code>{code}</code>}</pre>
      ) : (
        // The plain block is the fallback, so the code is readable immediately and simply gains
        // colour once the chunk lands — no empty flash.
        <Suspense fallback={<pre className={cn(PRE_CLASSES, className)}>{code}</pre>}>
          <CodeBlockHighlighted
            code={code}
            language={grammar as string}
            className={className}
            preClasses={PRE_CLASSES}
          />
        </Suspense>
      )}
    </div>
  );
};
